import axios from 'axios'

const BASE = 'https://api2.yaduo.com'
const POPUP_BASE = 'https://miniapp.yaduo.com'

const APP_VER = '3.35.1'
const CHANNEL_ID = '20001'
const PLAT_TYPE = '2'

// ---------- 工具 ----------

// 生成一个合法的 18 位身份证号（弹窗检测需要随机新客身份）
export function randomIdCard() {
  const region = '110101' // 北京市东城区
  const year = 1970 + Math.floor(Math.random() * 30)
  const month = String(1 + Math.floor(Math.random() * 12)).padStart(2, '0')
  const day = String(1 + Math.floor(Math.random() * 28)).padStart(2, '0')
  const seq = String(Math.floor(Math.random() * 999)).padStart(3, '0')
  const base = `${region}${year}${month}${day}${seq}`
  const weights = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2]
  const codes = ['1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2']
  let sum = 0
  for (let i = 0; i < 17; i++) sum += Number(base[i]) * weights[i]
  return base + codes[sum % 11]
}

const toNum = (v, fallback = 0) => {
  const n = Number(v)
  return Number.isNaN(n) ? fallback : n
}

const ratePrice = (r) => toNum(r.showPriceV2, NaN) || toNum(r.showPrice, NaN) || toNum(r.price, NaN) || NaN

// rate 上的普通标签（tags[].tagName）
const rateTags = (r) => {
  const tagNames = Array.isArray(r.tags) ? r.tags.map((t) => String(t?.tagName || t || '').trim()) : []
  return Array.from(new Set(tagNames.filter(Boolean)))
}

// rate 上的优惠券文案（discountTexts / discountTextsV2）
const rateDiscountTexts = (r) => {
  const texts = [
    ...(Array.isArray(r.discountTexts) ? r.discountTexts : []),
    ...(Array.isArray(r.discountTextsV2) ? r.discountTextsV2 : [])
  ].map((t) => String(t || '').trim())
  return Array.from(new Set(texts.filter(Boolean)))
}

// 只要 rate 的 tags 里出现「旋风升房」即视为可升房
const isUpgradeRate = (r) => rateTags(r).some((t) => t.includes('旋风升房'))

// ---------- 地点搜索 ----------

export async function searchPlaces(keyword) {
  const res = await axios.get(`${BASE}/atourlife/placeSearch/search`, {
    params: {
      activeId: '0',
      activitySource: '',
      appVer: '3.16.4',
      channelId: CHANNEL_ID,
      deviceId: '4AF1D83D-994A-4E21-B28E-C27B8FD5314B',
      inactiveId: '',
      keyWord: keyword,
      latitude: '',
      longitude: '',
      platType: PLAT_TYPE,
      token: ''
    }
  })
  const list = res.data?.result?.currentCity || []
  return list
    .filter((it) => it.chainId != null)
    .map((it) => ({ label: it.title, value: String(it.chainId), address: it.address || '' }))
}

// ---------- 单账号查价 ----------

export async function fetchChainDetail({ chainId, beginDate, endDate, token }) {
  const params = {
    platType: PLAT_TYPE,
    appVer: APP_VER,
    inactiveId: '',
    channelId: CHANNEL_ID,
    activitySource: '',
    activeId: '',
    token
  }
  const body = {
    sortByPriceWithCoupon: '1',
    chainId,
    delegatorId: '',
    endDate,
    delegatorMebId: '',
    beginDate,
    corporationId: '',
    token
  }
  const res = await axios.post(`${BASE}/atourlife/chain/chainDetailQuote`, body, {
    headers: { 'Content-Type': 'application/json' },
    params
  })
  return res.data?.result || null
}

// ---------- 优惠券检测（不需要 token）----------

export async function fetchChainCoupons({ chainId }) {
  const url = `${BASE}/atourlife/coupon/center/list/forChain`
  const res = await axios.get(url, {
    headers: { Accept: '*/*' },
    params: {
      platType: PLAT_TYPE,
      appVer: APP_VER,
      channelId: CHANNEL_ID,
      'At-Platform-Type': PLAT_TYPE,
      'At-App-Version': APP_VER,
      'At-Channel-Id': CHANNEL_ID,
      deviceId: '',
      token: '',
      chainId,
      inactiveId: '',
      activitySource: '',
      activeId: ''
    }
  })
  const list = Array.isArray(res.data?.result) ? res.data.result : []
  return list
    .filter((it) => it.chainCouponMsgDTO)
    .map((it) => {
      const dto = it.chainCouponMsgDTO || {}
      return {
        name: String(dto.chainCouponDesc || it.couponName || '').trim(),
        value: toNum(dto.couponValue, 0),
        canGet: Boolean(it.canGet),
        expiry: String(it.expiryStr || '').trim(),
        roomTypeLimit: String(it.roomTypeLimitStr || '').trim()
      }
    })
    .filter((c) => c.name || c.value > 0)
}

// ---------- 新客弹窗检测（独立 token）----------

export async function checkNewGuestPopup({ chainId, token }) {
  const res = await axios.post(
    `${POPUP_BASE}/atourlife/chain/newGuestIdentityCheck`,
    {
      docType: 1,
      realName: '刘三',
      idCardNumber: randomIdCard(),
      chainId
    },
    {
      headers: { 'Content-Type': 'application/json' },
      params: {
        token,
        platType: '5',
        appVer: '3.31.0',
        channelId: '300001',
        activitySource: '',
        activityId: '',
        activeId: ''
      }
    }
  )
  // result 有内容即为命中弹窗
  return Boolean(res.data?.result)
}

// ---------- 多账号聚合 ----------

// 把多个账号的查价结果聚合成按房型分组、组内策略按价格升序的列表
export function aggregateRooms(detailResults) {
  // detailResults: [{ account, result }]
  const roomMap = new Map()

  for (const { account, result } of detailResults) {
    const rooms = result?.priceResponse?.chainRoomList || []
    for (const room of rooms) {
      const info = room.roomTypeInfoResponse || {}
      const key = String(info.roomTypeId || info.roomTypeName || Math.random())
      if (!roomMap.has(key)) {
        roomMap.set(key, {
          roomTypeId: info.roomTypeId,
          roomTypeName: info.roomTypeName || '未知房型',
          marketPrice: 0,
          rates: []
        })
      }
      const agg = roomMap.get(key)

      const rateItems = Array.isArray(room.roomPriceList) && room.roomPriceList.length
        ? room.roomPriceList
        : (room.minRoomPrice ? [room.minRoomPrice] : [])

      for (const r of rateItems) {
        const price = ratePrice(r)
        if (!price || Number.isNaN(price)) continue
        const market = toNum(r.marketPrice, 0)
        agg.rates.push({
          account,
          name: String(r.rateCodeName || '标准预订'),
          price,
          marketPrice: market,
          stock: toNum(r.leftRoomNum, NaN) || toNum(r.roomNum, 0),
          canUpgrade: isUpgradeRate(r),
          breakfast: toNum(r.breakFastNum, 0),
          tags: rateTags(r),
          discountTexts: rateDiscountTexts(r),
          cancelTips: String(r.cancelTips || '').trim()
        })
      }
    }
  }

  const rooms = Array.from(roomMap.values()).map((agg) => {
    // 同房型跨账号去重 + 按价格升序
    const seen = new Set()
    const rates = agg.rates
      .sort((a, b) => a.price - b.price)
      .filter((r) => {
        const sig = `${r.account}|${r.name}|${r.price}`
        if (seen.has(sig)) return false
        seen.add(sig)
        return true
      })
    const lowestPrice = rates.length ? rates[0].price : 0
    const stock = rates.reduce((m, r) => Math.max(m, r.stock || 0), 0)
    const canUpgrade = rates.some((r) => r.canUpgrade)
    // 协议门市价与普通门市价不一致，统一取各 rate 的最低门市价计算
    const markets = rates.map((r) => r.marketPrice).filter((m) => m > 0)
    const marketPrice = markets.length ? Math.min(...markets) : 0
    return {
      ...agg,
      rates,
      lowestPrice,
      stock,
      canUpgrade,
      marketPrice
    }
  })

  // 房型按最低实价升序
  return rooms.filter((r) => r.rates.length).sort((a, b) => a.lowestPrice - b.lowestPrice)
}

// 报价公式：门市价 × k + b
export function calcQuote(marketPrice, config, hasPopup) {
  const market = toNum(marketPrice, 0)
  if (!market) return 0
  const k = hasPopup ? toNum(config.popupK, 1) : toNum(config.nonPopupK, 1)
  const b = hasPopup ? toNum(config.popupB, 0) : toNum(config.nonPopupB, 0)
  return Math.round(market * k + b)
}
